'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import { getComponentProps } from '@wix/thunderbolt-elements/src/components/ConsentPolicyWrapper/viewer/utils';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import { useMap, APIProvider, Map } from '@vis.gl/react-google-maps';
import type {
  GoogleMapProps,
  IGoogleMapsImperativeActions,
} from '../GoogleMap.types';
import GoogleMapMarker from './GoogleMapMarker';
import { createImperativeMethods } from './utils/imperativeMethods';
import { isValidZoom } from './utils/utils';

import semanticClassNames from '../GoogleMap.semanticClassNames';
import style from './style/GoogleMapReact.module.scss';
import { API_KEY, DEFAULT_ZOOM, MAP_IDS, MAP_TYPES } from '../constants';
import { useCallback } from 'react';

const GoogleMapReact: React.ForwardRefRenderFunction<
  IGoogleMapsImperativeActions,
  GoogleMapProps
> = (props, forwardRef) => {
  const {
    id,
    className,
    customClassNames = [],
    skin,
    mapData,
    translations,
    isConsentPolicyActive,
    onUpdateCenter,
    onUpdateZoom,
    onMarkerClicked,
    onMapClicked,
    onMouseEnter,
    onMouseLeave,
    language,
    onMapMount,
    lang,
  } = props;

  const map = useMap();
  const mapRef = React.useRef<google.maps.Map | null>(map);
  const [mapZoom, setMapZoom] = React.useState(
    () => mapData.zoom || DEFAULT_ZOOM,
  );
  const [openInfoWindowIndex, setOpenInfoWindowIndex] = React.useState<
    number | null
  >(0);
  const [mapCenter, setMapCenter] = React.useState(() => ({
    lat: mapData.locations[0]?.latitude ?? 0,
    lng: mapData.locations[0]?.longitude ?? 0,
  }));

  const handleZoomChange = useCallback(
    (rawZoom: number) => {
      const newZoom = Math.round(rawZoom);
      if (newZoom === mapZoom || !isValidZoom(newZoom)) {
        return;
      }
      setMapZoom(newZoom);
      onUpdateZoom?.({ zoom: newZoom });
    },
    [onUpdateZoom, mapZoom],
  );

  const handleCenterChanged = React.useCallback(
    (center: google.maps.LatLngLiteral) => {
      if (
        center === undefined ||
        center === null ||
        center.lat === mapCenter.lat ||
        center.lng === mapCenter.lng
      ) {
        return;
      }
      setMapCenter(center);
      onUpdateCenter?.({ latitude: center.lat, longitude: center.lng });
    },
    [onUpdateCenter, mapCenter],
  );

  const handleMapClick = React.useCallback(
    (event: MapMouseEvent) => {
      try {
        if (onMapClicked && event.detail.latLng) {
          onMapClicked({
            type: 'mapClicked',
            location: {
              latitude: event.detail.latLng.lat,
              longitude: event.detail.latLng.lng,
            },
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('GoogleMap: Click handler error', {
          error: error instanceof Error ? error.message : String(error),
          componentId: id,
        });
      }
    },
    [onMapClicked, id],
  );

  const handleMarkerClicked = (index: number) => (data: any) => {
    setOpenInfoWindowIndex(openInfoWindowIndex === index ? null : index);
    onMarkerClicked?.(data);
  };

  React.useImperativeHandle(
    forwardRef,
    () =>
      createImperativeMethods(
        mapRef,
        handleZoomChange,
        handleCenterChanged,
        mapData.locations,
      ),
    [handleZoomChange, handleCenterChanged, mapData.locations],
  );

  React.useEffect(() => {
    onMapMount?.();
  }, [onMapMount]);

  const mapStyleConfig = React.useMemo(() => {
    const styleKey = mapData.mapStyleKey as keyof typeof MAP_IDS;
    return {
      mapId: MAP_IDS[styleKey] || MAP_IDS.CLASSIC,
      mapTypeId: (MAP_TYPES[styleKey] || MAP_TYPES.CLASSIC).toLowerCase(),
    };
  }, [mapData.mapStyleKey]);

  const mapProps = React.useMemo(
    () => ({
      fullscreenControl: false,
      clickableIcons: true,
      keyboardShortcuts: true,
      disableDoubleClickZoom: false,
      disableDefaultUI: true,
      gestureHandling: mapData.mapInteractive ? 'auto' : 'none',
      mapTypeControl: mapData.showMapType,
      zoomControl: mapData.showZoom,
      streetViewControl: mapData.showStreetView,
    }),
    [
      mapData.mapInteractive,
      mapData.showMapType,
      mapData.showZoom,
      mapData.showStreetView,
    ],
  );

  const domSafeProps = {
    id,
    className: classNames(
      style[skin],
      className,
      formatClassNames(semanticClassNames.root, ...customClassNames),
    ),
    ...getDataAttributes(props),
    lang,
  };

  const baseComponentProps = getComponentProps(
    isConsentPolicyActive,
    domSafeProps,
  );

  return (
    <div
      {...baseComponentProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={style.mapContainer}
        aria-label={translations.title}
        role="region"
      >
        <APIProvider apiKey={API_KEY} language={language}>
          <Map
            onCenterChanged={e => handleCenterChanged(e.detail.center)}
            onZoomChanged={e => handleZoomChange(e.detail.zoom)}
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={mapCenter}
            defaultZoom={mapZoom}
            {...mapProps}
            {...mapStyleConfig}
          >
            {mapData.locations.map((location, index) => (
              <GoogleMapMarker
                key={`${index}-${location.latitude}-${location.longitude}`}
                location={location}
                infoWindowOpen={openInfoWindowIndex === index}
                onMarkerClick={handleMarkerClicked(index)}
                onInfoWindowClose={() => setOpenInfoWindowIndex(null)}
                showDirectionLink={mapData.showDirectionsLink}
                allyTargetBlankDescription={
                  translations.allyTargetBlankDescription
                }
              />
            ))}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
};

export default React.memo(React.forwardRef(GoogleMapReact));
